/**
 * A modal built on the native <dialog>: focus is trapped and restored, Escape closes it, and the
 * rest of the page is inert, all by the browser. Used only for short tasks that need protected
 * focus (reporting, claiming, merging), never for reading.
 */
import { useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'

export function Dialog({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  // A record page holds several dialogs (report, merge, claim); each needs its own title id.
  const titleId = useId()
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal?.()
    if (!open && d.open) d.close()
  }, [open])
  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
    >
      <div className="dialog-body">
        <header className="dialog-head">
          <h2 id={titleId} className="panel-title">
            {title}
          </h2>
          <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        {open && children}
      </div>
    </dialog>
  )
}
